import { Alert, AlertTitle, Box, Link, Stack, Typography } from "@mui/material";
import { IssuesDataGrid, IssueWithUrl } from "components/IssuesDataGrid";
import { CREDENTIAL_REQUIRED_ACTIVITY_DESCRIPTION } from "encamp-shared/src/constants/tierii";
import { PresubmissionError } from "encamp-shared/src/presubmission/types";
import { ActivityType, GetTierIiReportQuery } from "generated-graphql/graphql";

type Activity = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["currentWorkflow"]
>["activities"][0];

type Facility = GetTierIiReportQuery["tierIIReport"]["facility"];
type Tenant = GetTierIiReportQuery["tierIIReport"]["tenant"];

type PresubmissionOutput = {
  issues?: IssueWithUrl[];
  validationBypassed?: boolean;
  bypassReason?: "state" | "facility";
  errors?: PresubmissionError[];
};

type ActivityErrorDisplayProps = {
  activity: Activity;
  facility?: Facility | null;
  tenant?: Tenant | null;
};

const facilityCredentialsUrlFor = (
  facility: Facility | null | undefined,
  tenant: Tenant | null | undefined
): string | null =>
  facility?.id != null && tenant?.id != null
    ? `/o/${tenant.id}/facilities/${facility.id}/credentials`
    : null;

/**
 * Use this component to define a custom display for activity errors.
 *
 * For example, presubmission activities have issues as part of their
 * output so we have a custom display for those.
 */
export const ActivityErrorDisplay = ({ activity, facility, tenant }: ActivityErrorDisplayProps) => {
  const facilityCredentialsUrl = facilityCredentialsUrlFor(facility, tenant);

  if (activity.type === ActivityType.Presubmission) {
    const output = activity.output as PresubmissionOutput | undefined;
    const issues = (output?.issues ?? []) as IssueWithUrl[];
    const validationBypassed = output?.validationBypassed ?? false;
    const bypassReason = output?.bypassReason;
    const errors = output?.errors ?? [];

    return (
      <Stack sx={{ flex: 1 }} gap={1}>
        {validationBypassed && (
          <Alert severity="info">Validation was bypassed for this {bypassReason}.</Alert>
        )}
        {errors.length > 0 && (
          <Stack gap={1}>
            {errors.map((error, idx) => (
              <PresubmissionErrorAlert
                key={`${error.type}-${idx}`}
                error={error}
                facilityCredentialsUrl={facilityCredentialsUrl}
              />
            ))}
          </Stack>
        )}
        {issues.length > 0 && (
          <IssuesDataGrid issues={issues} validationBypassed={validationBypassed} />
        )}
      </Stack>
    );
  }

  if (
    activity.type === ActivityType.Error &&
    activity.description === CREDENTIAL_REQUIRED_ACTIVITY_DESCRIPTION
  ) {
    return (
      <Box sx={{ flex: 1 }} component="section">
        <Typography variant="body1" sx={{ mb: 2 }}>
          The submission workflow for this report cannot be created because the facility needs to
          have a single Tier II credential.
        </Typography>
        <Stack component="ol" sx={{ pl: 2.5, m: 0 }} spacing={1}>
          <Typography component="li" variant="body2">
            {facilityCredentialsUrl != null ? (
              <Link href={facilityCredentialsUrl} target="_blank" rel="noopener noreferrer">
                Update the Tier II credential(s) for this facility
              </Link>
            ) : null}
          </Typography>
          <Typography component="li" variant="body2">
            After ensuring the facility has a single Tier II credential, use{" "}
            <strong>Reset Report</strong> in the left sidebar and force cancel the workflow so that
            a new workflow can be created.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return <></>;
};

type PresubmissionErrorAlertProps = {
  error: PresubmissionError;
  facilityCredentialsUrl: string | null;
};

const PresubmissionErrorAlert = ({
  error,
  facilityCredentialsUrl,
}: PresubmissionErrorAlertProps) => {
  const credentialsLink =
    facilityCredentialsUrl != null ? (
      <Link href={facilityCredentialsUrl} target="_blank" rel="noopener noreferrer">
        Open facility credentials
      </Link>
    ) : null;

  switch (error.type) {
    case "credential_missing_facility":
      return (
        <Alert severity="error">
          <AlertTitle>Credential is missing this facility in the portal</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error.message}
          </Typography>
          <Typography variant="body2">
            Add this facility to the credential&apos;s account in the portal, then re-run{" "}
            <strong>verifyCredentials</strong> so the manifest is refreshed.
            {credentialsLink != null && <> {credentialsLink}.</>}
          </Typography>
        </Alert>
      );
    case "credential_manifest_unverified":
      return (
        <Alert severity="error">
          <AlertTitle>Credential has not been verified with portal scraping yet</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error.message}
          </Typography>
          <Typography variant="body2">
            Trigger <strong>verifyCredentials</strong> to populate the portal facility manifest.
            {credentialsLink != null && <> {credentialsLink}.</>}
          </Typography>
        </Alert>
      );
    case "credential_implementation_missing":
      return (
        <Alert severity="error">
          <AlertTitle>Credential implementation has not been started</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error.message}
          </Typography>
          {credentialsLink != null && (
            <Typography variant="body2">{credentialsLink} to begin implementation.</Typography>
          )}
        </Alert>
      );
    case "credential_implementation_incomplete":
      return (
        <Alert severity="error">
          <AlertTitle>Credential implementation is not COMPLETE</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error.message}
          </Typography>
          {credentialsLink != null && (
            <Typography variant="body2">
              {credentialsLink} to advance the implementation status.
            </Typography>
          )}
        </Alert>
      );
    case "credential_not_found":
      return (
        <Alert severity="error">
          <AlertTitle>No credential is linked to this facility</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error.message}
          </Typography>
          {credentialsLink != null && (
            <Typography variant="body2">{credentialsLink} to assign one.</Typography>
          )}
        </Alert>
      );
    case "credential_verification_failed":
      return (
        <Alert severity="error">
          <AlertTitle>Credential failed verification</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error.message}
          </Typography>
          {credentialsLink != null && (
            <Typography variant="body2">
              {credentialsLink} to fix the credential and re-run verification.
            </Typography>
          )}
        </Alert>
      );
    default:
      return (
        <Alert severity="error">
          <Typography variant="body2">{error.message}</Typography>
        </Alert>
      );
  }
};
